import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import NumberInput from "@/components/base/NumberInput";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { InventoryItem, Product } from "@/types";
import { formatCOP, formatNumber } from "@/utils/format";
import type { PriceAlert } from "@/utils/accounting";

type Mode = "ajuste" | "traspaso";

interface InventoryEditModalProps {
  open: boolean;
  product: Product | null;
  inventory: InventoryItem[];
  // Catálogo completo (para elegir el producto destino de un traspaso).
  products: Product[];
  alert?: PriceAlert | null;
  onClose: () => void;
  // Ajuste HACIA ABAJO: requiere motivo y deja constancia (producto, antes, después, razón, usuario).
  onAdjust: (item: InventoryItem, reason: string) => void;
  // Un aumento de stock se hace comprando: se redirige al flujo de compra.
  onRequestBuy: (product: Product) => void;
  // Traspaso de unidades hacia otro producto (no genera compra/venta ni movimiento contable).
  onTransfer: (target: Product, qty: number, reason: string) => void | Promise<void>;
}

// Edición del stock del admin (Oficina). Dos modos:
//  · AJUSTE: subir stock → se hace COMPRANDO; bajar stock → se pide el MOTIVO y
//    queda constancia.
//  · TRASPASO: mover unidades de este producto a OTRO (ej. reubicar mercancía
//    heredada). No suma a movimientos ni contabilidad; deja constancia del motivo.
const InventoryEditModal = ({
  open,
  product,
  inventory,
  products,
  alert,
  onClose,
  onAdjust,
  onRequestBuy,
  onTransfer,
}: InventoryEditModalProps) => {
  const [mode, setMode] = useState<Mode>("ajuste");

  // Modo ajuste
  const [stockCasa, setStockCasa] = useState(0);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  // Modo traspaso
  const [targetId, setTargetId] = useState("");
  const [transferQty, setTransferQty] = useState(1);
  const [transferReason, setTransferReason] = useState("");
  const [transferError, setTransferError] = useState("");

  const current = product
    ? inventory.find((i) => i.productId === product.id)?.stockCasa ?? 0
    : 0;

  useEffect(() => {
    if (!open || !product) return;
    const item = inventory.find((i) => i.productId === product.id);
    setStockCasa(item?.stockCasa ?? 0);
    setReason("");
    setError("");
    setMode("ajuste");
    setTargetId("");
    setTransferQty(1);
    setTransferReason("");
    setTransferError("");
  }, [open, product, inventory]);

  const targetOptions = useMemo(
    () => products.filter((p) => p.id !== product?.id),
    [products, product]
  );

  if (!open || !product) {
    return (
      <Modal open={open} onClose={onClose} title="Inventario del producto" icon="ri-archive-2-line">
        <p className="text-sm text-foreground-600">Selecciona un producto.</p>
      </Modal>
    );
  }

  const increasing = stockCasa > current;
  const decreasing = stockCasa < current;
  const targetProduct = targetOptions.find((p) => p.id === targetId) ?? null;

  const saveAdjust = () => {
    if (increasing) return;
    if (decreasing && !reason.trim()) {
      setError("Escribe el motivo del ajuste (por qué baja el stock).");
      return;
    }
    const item = inventory.find((i) => i.productId === product.id);
    onAdjust(
      {
        productId: product.id,
        stockCasa: Number(stockCasa) || 0,
        stockPuntoClear: item?.stockPuntoClear ?? 0,
        stockByAdvisor: item?.stockByAdvisor ?? {},
        stockOwnedByAdvisor: item?.stockOwnedByAdvisor ?? {},
      },
      reason.trim()
    );
  };

  const saveTransfer = () => {
    if (!targetProduct) {
      setTransferError("Selecciona el producto de destino.");
      return;
    }
    if (transferQty <= 0) {
      setTransferError("La cantidad debe ser mayor a cero.");
      return;
    }
    if (transferQty > current) {
      setTransferError(`Solo hay ${formatNumber(current)} und en Oficina para traspasar.`);
      return;
    }
    if (transferReason.trim().length < 3) {
      setTransferError("Escribe el motivo del traspaso.");
      return;
    }
    void onTransfer(targetProduct, transferQty, transferReason.trim());
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Inventario del producto"
      icon="ri-archive-2-line"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          {mode === "ajuste" ? (
            <button
              onClick={saveAdjust}
              disabled={increasing}
              className={`${primaryButtonClass} ${increasing ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <i className="ri-save-3-line" /> Guardar ajuste
            </button>
          ) : (
            <button onClick={saveTransfer} className={primaryButtonClass}>
              <i className="ri-swap-box-line" /> Traspasar unidades
            </button>
          )}
        </>
      }
    >
      {/* Selector de modo */}
      <div className="inline-flex items-center gap-1 p-1 mb-4 rounded-full border border-background-300/50 bg-background-200/40">
        <button
          onClick={() => setMode("ajuste")}
          className={`px-3.5 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-colors ${
            mode === "ajuste"
              ? "bg-accent-500/25 text-accent-100"
              : "text-foreground-600 hover:text-foreground-900"
          }`}
        >
          <i className="ri-equalizer-line mr-1" /> Ajuste
        </button>
        <button
          onClick={() => setMode("traspaso")}
          className={`px-3.5 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-colors ${
            mode === "traspaso"
              ? "bg-accent-500/25 text-accent-100"
              : "text-foreground-600 hover:text-foreground-900"
          }`}
        >
          <i className="ri-swap-box-line mr-1" /> Traspaso
        </button>
      </div>

      <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2 mb-4">
        <p className="text-sm font-semibold text-foreground-950">{product.name}</p>
        <p className="text-[11px] font-mono text-accent-200">
          {product.sku} · {formatCOP(product.pricePublic)}
        </p>
      </div>

      {alert && (
        <div className="rounded-md bg-secondary-500/15 border border-secondary-500/50 px-3 py-2.5 mb-4 flex items-start gap-2.5 animate-fade-up">
          <span className="w-7 h-7 rounded-md bg-secondary-500/25 text-secondary-100 flex items-center justify-center shrink-0">
            <i className="ri-alert-line" />
          </span>
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-secondary-100">Aviso de desfase de precio</p>
            <p className="text-[11px] text-foreground-700 mt-0.5">{alert.message}</p>
          </div>
        </div>
      )}

      <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2 mb-4 flex items-center justify-between">
        <span className="text-[11px] text-foreground-600">Stock actual en Oficina</span>
        <span className="text-sm font-bold text-foreground-950">{formatNumber(current)} und</span>
      </div>

      {mode === "ajuste" && (
        <>
          <div>
            <label className={labelClass}>Nuevo stock en Oficina (admin)</label>
            <NumberInput min={0} value={stockCasa} onChange={setStockCasa} className={inputClass} />
          </div>

          {/* Subir stock: no se digita, se compra. */}
          {increasing && (
            <div className="mt-3 rounded-md bg-primary-500/12 border border-primary-400/40 px-3.5 py-3 animate-fade-up">
              <div className="flex items-start gap-2.5">
                <span className="w-8 h-8 rounded-md bg-primary-500/25 text-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-shopping-cart-2-line" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-foreground-950">
                    Para aumentar el stock hay que comprar
                  </p>
                  <p className="text-[11px] text-foreground-700 mt-0.5">
                    El aumento de inventario entra por una compra (queda en contabilidad y suma PV).
                    Usa el flujo de compra para ingresar {formatNumber(stockCasa - current)} und más.
                  </p>
                  <button
                    type="button"
                    onClick={() => onRequestBuy(product)}
                    className="mt-2 px-3 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                  >
                    <i className="ri-add-circle-line" /> Comprar para stock
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bajar stock: se pide el motivo y se deja constancia. */}
          {decreasing && (
            <div className="mt-3">
              <label className={labelClass}>Motivo del ajuste (obligatorio)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={300}
                rows={3}
                placeholder="Ej: producto vencido, dañado, cuadre de conteo físico..."
                className={`${inputClass} resize-none`}
              />
              <p className="text-[10px] text-foreground-500 mt-1">
                Se restan {formatNumber(current - stockCasa)} und y queda la constancia de este ajuste.
              </p>
            </div>
          )}

          {!increasing && !decreasing && (
            <p className="text-[11px] text-foreground-500 mt-3 flex items-center gap-1.5">
              <i className="ri-information-line" /> El stock no cambió. Ajusta la cantidad para registrar
              un movimiento.
            </p>
          )}

          {error && (
            <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
              <i className="ri-error-warning-line" /> {error}
            </p>
          )}

          <div className="mt-4 rounded-md bg-secondary-500/10 border border-secondary-500/25 px-3 py-2.5 flex items-start gap-2">
            <i className="ri-information-line text-secondary-200 mt-0.5" />
            <p className="text-[11px] text-foreground-700">
              ¿Necesitas mover estas unidades a OTRO producto? Usa la pestaña{" "}
              <span className="font-semibold text-foreground-900">Traspaso</span>.
            </p>
          </div>
        </>
      )}

      {mode === "traspaso" && (
        <>
          <div className="mb-3">
            <label className={labelClass}>Producto de destino</label>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className={`${inputClass} cursor-pointer`}
            >
              <option value="">Selecciona el producto que recibe las unidades…</option>
              {targetOptions.map((p) => {
                const stock = inventory.find((i) => i.productId === p.id)?.stockCasa ?? 0;
                return (
                  <option key={p.id || p.sku} value={p.id}>
                    {p.name} · {p.sku} ({stock} und)
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className={labelClass}>Cantidad a traspasar</label>
            <NumberInput
              min={1}
              max={current}
              value={transferQty}
              onChange={setTransferQty}
              className={inputClass}
            />
            <p className="text-[10px] text-foreground-500 mt-1">
              Disponible en Oficina: {formatNumber(current)} und.
            </p>
          </div>

          <div className="mt-3">
            <label className={labelClass}>Motivo del traspaso (obligatorio)</label>
            <textarea
              value={transferReason}
              onChange={(e) => setTransferReason(e.target.value)}
              maxLength={300}
              rows={3}
              placeholder="Ej: producto heredado / cambió de nombre en el catálogo, unificar referencias..."
              className={`${inputClass} resize-none`}
            />
          </div>

          {targetProduct && transferQty > 0 && (
            <div className="mt-3 rounded-md bg-accent-500/10 border border-accent-500/25 px-3 py-2.5 text-[11px] text-foreground-700">
              Se moverán <span className="font-semibold text-foreground-900">{formatNumber(transferQty)} und</span> de{" "}
              <span className="font-semibold text-foreground-900">{product.name}</span> a{" "}
              <span className="font-semibold text-foreground-900">{targetProduct.name}</span>.
            </div>
          )}

          {transferError && (
            <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
              <i className="ri-error-warning-line" /> {transferError}
            </p>
          )}

          <div className="mt-4 rounded-md bg-secondary-500/10 border border-secondary-500/25 px-3 py-2.5 flex items-start gap-2">
            <i className="ri-information-line text-secondary-200 mt-0.5" />
            <p className="text-[11px] text-foreground-700">
              El traspaso solo mueve unidades entre productos: no registra compra, venta ni
              movimiento contable. Queda la constancia del motivo para el historial.
            </p>
          </div>
        </>
      )}
    </Modal>
  );
};

export default InventoryEditModal;